import { useState } from "react";
import { OrgId, View } from "../types";
import type { Hub } from "../lib/useHub";
import type { GuideList } from "../types";
import { navigate } from "../lib/router";
import { guideHref, guidePathParts } from "../lib/guides";
import { orgLabel } from "../lib/markdown";
import { useOrgCounts } from "../lib/useOrgCounts";
import { useCommonCount } from "../lib/useCommonCount";
import { useRuntimeCount } from "../lib/useRuntimeCount";
import { useMcpCount } from "../lib/useMcpCount";
import { STORAGE_KEYS } from "../lib/storageKeys";
import { isAppAdmin, useMe } from "../lib/useMe";
import UserFooter from "./UserFooter";
import KnowledgeBaseNav from "./KnowledgeBaseNav";

// A small count pill shown on org-scoped nav items (undefined while loading).
function NavCount({ value }: { value?: number }) {
  if (value === undefined) return null;
  return <span className="nav-count">{value}</span>;
}

// Persist the collapsed state so the rail width survives reloads.
const COLLAPSE_KEY = STORAGE_KEYS.sidebarCollapsed;

// Leftmost rail: the global views (Agents Hub, …) on top — they are not
// org-specific — then the org selector and the per-org views below it.
// Collapses to a thin icon-only rail that expands on hover (transient preview)
// or when a menu item is clicked (pins it open). The collapse toggle sits
// inline with the Home row.
export default function Sidebar({
  org,
  orgs,
  view,
  hub,
  guides,
  onOrg,
  onView,
  noOrgs = false,
}: {
  org: OrgId;
  /** Orgs to showcase (those whose knowledge base is available). */
  orgs: OrgId[];
  view: View;
  hub: Hub;
  /** The org's internal guide collections (undefined while loading). */
  guides?: GuideList;
  onOrg: (org: OrgId) => void;
  onView: (view: View) => void;
  /** Loaded and this user has access to no orgs (tenant scope empty). */
  noOrgs?: boolean;
}) {
  const me = useMe();
  // The Repositories, Knowledge Graph and Agents views expose the knowledge
  // base's full repo/agent/graph inventory, so they're Omni Hub admin-only —
  // server-enforced (the API 403s non-admins); hiding the nav items just keeps
  // the UI honest.
  const admin = isAppAdmin(me);
  const counts = useOrgCounts(org, admin);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSE_KEY) === "1");

  function setCollapsedPersist(next: boolean) {
    setCollapsed(next);
    localStorage.setItem(COLLAPSE_KEY, next ? "1" : "0");
  }

  // Clicking a menu item navigates and pins the rail open (per the spec:
  // "expanded on hover or click on menu item").
  function goView(v: View) {
    if (collapsed) setCollapsedPersist(false);
    onView(v);
  }

  // Live counts for the Agents Hub sub-items. Left `undefined` (→ no pill) until
  // the board has actually loaded, so we never flash a misleading "0" while the
  // session/task maps are still empty — a real 0 only shows once loaded.
  const sessionCount = hub.boardLoaded ? Object.keys(hub.sessions).length : undefined;
  const taskCount = hub.boardLoaded ? Object.keys(hub.tasks).length : undefined;
  const commonCount = useCommonCount();
  const runtimeCount = useRuntimeCount(admin);
  const mcpCount = useMcpCount(org, admin);
  const devhubCount = (counts.devhub ?? 0) + (counts.apiRef ?? 0);
  const devhubCountValue = counts.devhub === undefined && counts.apiRef === undefined ? undefined : devhubCount;
  const showDevhub = devhubCount > 0 || view === View.Devhub;
  const activeGuide = guidePathParts(window.location.pathname).collection;
  return (
    <aside className={`sidebar${collapsed ? " collapsed" : ""}`}>
      <div className="sidebar-inner">
        <nav className="nav-primary">
          {/* Global views — not org-specific — sit above the org selector. */}
          {/* Home dashboard — the landing overview across hub, common, docs, and orgs.
              The collapse toggle sits inline with this row, on the right. */}
          <div className="nav-home-row">
            <button className={`nav-link${view === View.Home ? " active" : ""}`} onClick={() => goView(View.Home)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 10.5 12 3l9 7.5" /><path d="M5 9.5V21h14V9.5" /><path d="M9.5 21v-6h5v6" /></svg>
              <span className="nav-link-label">Home</span>
            </button>
            <button
              className="nav-collapse-btn"
              onClick={() => setCollapsedPersist(!collapsed)}
              title={collapsed ? "Expand menu" : "Collapse menu"}
              aria-label={collapsed ? "Expand menu" : "Collapse menu"}
              aria-pressed={collapsed}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="m11 17-5-5 5-5" /><path d="m18 17-5-5 5-5" /></svg>
            </button>
          </div>

          {/* Agents Hub — a parent grouping its overview (root) and its sub-pages:
              Sessions, Tasks, Common, and (admins only) Runtime. The sub-nav is
              always shown. Runtime names every repo with agent content and can
              launch containers, so it's gated like Repositories/Agents —
              server-enforced (the API 403s non-admins); hiding it keeps the UI
              honest. */}
          <button
            className={`nav-link nav-parent${view === View.Hub ? " active" : ""}`}
            onClick={() => goView(View.Hub)}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="2.6" /><circle cx="5" cy="5" r="1.9" /><circle cx="19" cy="5" r="1.9" /><circle cx="5" cy="19" r="1.9" /><circle cx="19" cy="19" r="1.9" /><path d="M10.2 10.2 6.3 6.3M13.8 10.2l3.9-3.9M10.2 13.8l-3.9 3.9M13.8 13.8l3.9 3.9" /></svg>
            <span className="nav-link-label">Agents Hub</span>
          </button>

          <div className="nav-sub">
            <button className={`nav-sublink${view === View.Sessions ? " active" : ""}`} onClick={() => goView(View.Sessions)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
              <span className="nav-link-label">Sessions</span>
              <NavCount value={sessionCount} />
            </button>
            <button className={`nav-sublink${view === View.Tasks ? " active" : ""}`} onClick={() => goView(View.Tasks)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></svg>
              <span className="nav-link-label">Tasks</span>
              <NavCount value={taskCount} />
            </button>
            <button className={`nav-sublink${view === View.Skills ? " active" : ""}`} onClick={() => goView(View.Skills)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></svg>
              <span className="nav-link-label">Common</span>
              <NavCount value={commonCount} />
            </button>
            {admin && (
              <button className={`nav-sublink${view === View.Runtime ? " active" : ""}`} onClick={() => goView(View.Runtime)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="7" rx="1.6" /><rect x="3" y="13" width="18" height="7" rx="1.6" /><path d="M7 7.5h.01M7 16.5h.01" /></svg>
                <span className="nav-link-label">Runtime</span>
                <NavCount value={runtimeCount} />
              </button>
            )}
          </div>

          {/* Divider: global views above, org-scoped section (selector + per-org views) below. */}
          <div className="nav-separator" />

          {/* Knowledge-base clone/map control — all served content comes from it, so
              it sits directly above the Organization selector. When a non-admin has
              been granted no orgs there's nothing here for them to browse or act on,
              so an access note takes its place. Admins keep the control even with zero
              orgs — cloning the KB is how orgs first appear. */}
          {noOrgs && me && !isAppAdmin(me) ? (
            <div className="nav-kb-empty" role="note">
              No organizations are available to your account yet. If you expect access,
              ask an Omni Hub admin to grant your user an org membership.
            </div>
          ) : (
            <KnowledgeBaseNav />
          )}

          {/* Org-scoped knowledge base — the selector + per-org views. Only orgs
              with content on disk are showcased (the KB must be synced first; per-org
              tokens add live repo cloning on top), so the section is hidden entirely
              when no org has content to browse. */}
          {orgs.length > 0 && (
            <>
              <div className="org-select">
                <label className="org-select-label" htmlFor="orgSelect">Organization</label>
                <select id="orgSelect" value={orgs.includes(org) ? org : orgs[0]} onChange={(e) => onOrg(e.target.value as OrgId)}>
                  {orgs.map((o) => (
                    <option key={o} value={o}>{orgLabel(o)}</option>
                  ))}
                </select>
              </div>

              {admin && (
                <button className={`nav-link nav-link-org${view === View.Repos ? " active" : ""}`} onClick={() => goView(View.Repos)}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z" /></svg>
                  <span className="nav-link-label">Repositories</span>
                  <NavCount value={counts.repos} />
                </button>
              )}

              <button className={`nav-link nav-link-org${view === View.Docs ? " active" : ""}`} onClick={() => goView(View.Docs)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
                <span className="nav-link-label">System Docs</span>
                <NavCount value={counts.docs} />
              </button>

              {/* Knowledge Graph — the Atlas. Sits under System Docs because it is the
                  same knowledge in a connected shape: one page per thing, with typed,
                  cited links between them. Omni Hub admin-only, like Repositories and
                  Agents — hiding it here is cosmetic; requireAdmin on every
                  /api/knowledge-graph route is what enforces it. */}
              {admin && (
                <button className={`nav-link nav-link-org${view === View.Graph ? " active" : ""}`} onClick={() => goView(View.Graph)}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="6" cy="6" r="2.5" /><circle cx="18" cy="7" r="2.5" /><circle cx="12" cy="17" r="2.5" /><path d="M8.1 7.2 15.9 6.4M7.2 8.1l3.6 6.7M16.9 9.2l-3.5 5.6" /></svg>
                  <span className="nav-link-label">Knowledge Graph</span>
                  <NavCount value={counts.graph} />
                </button>
              )}

              {/* DevHub: the public, customer-facing developer documentation. One page
                  — Docs / API Reference / pinned-section tabs live inside it (the
                  active tab is the URL segment). Visibility is derived from corpus
                  counts instead of a hardcoded org. */}
              {showDevhub && (
                <button
                  className={`nav-link nav-link-org${view === View.Devhub ? " active" : ""}`}
                  onClick={() => goView(View.Devhub)}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M3 12h18" /><path d="M12 3a14 14 0 0 1 0 18 14 14 0 0 1 0-18z" /></svg>
                  <span className="nav-link-label">DevHub</span>
                  <NavCount value={devhubCountValue} />
                </button>
              )}

              {/* Guides: the org's INTERNAL guide collections (content/<org>/internal/ in
                  the DevHub corpus repo — not part of any docs version, so neither DevHub
                  nor the public site shows them). A parent with one sub-link per
                  collection, in the corpus's order; shown only for orgs that have any. */}
              {(guides?.collections.length ?? 0) > 0 && guides && (
                <>
                  <button
                    className={`nav-link nav-link-org nav-parent${view === View.Guides ? " active" : ""}`}
                    onClick={() => goView(View.Guides)}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" /></svg>
                    <span className="nav-link-label">Guides</span>
                  </button>
                  <div className="nav-sub">
                    {guides.collections.map((c) => (
                      <button
                        key={c.id}
                        className={`nav-sublink${view === View.Guides && activeGuide === c.id ? " active" : ""}`}
                        onClick={() => {
                          if (collapsed) setCollapsedPersist(false);
                          void navigate(guideHref(org, c.id));
                        }}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><path d="M8 13h8M8 17h5" /></svg>
                        <span className="nav-link-label">{c.title}</span>
                        <NavCount value={c.pages.length} />
                      </button>
                    ))}
                  </div>
                </>
              )}

              {/* Agents: the per-repo .agents/ knowledge for the selected org. Sits
                  directly under System Docs as a sibling org-scoped view. */}
              {admin && (
                <button className={`nav-link nav-link-org${view === View.Agents ? " active" : ""}`} onClick={() => goView(View.Agents)}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="8" width="16" height="12" rx="2" /><path d="M12 8V4M9 4h6" /><circle cx="9" cy="14" r="1" /><circle cx="15" cy="14" r="1" /></svg>
                  <span className="nav-link-label">Agents</span>
                  <NavCount value={counts.agents} />
                </button>
              )}

              {/* MCP: the external MCP servers this org's internal Claude process can
                  use — admin-only, like Agents (every /api/mcp route is requireAdmin;
                  a connection carries another system's credential). */}
              {admin && (
                <button className={`nav-link nav-link-org${view === View.Mcp ? " active" : ""}`} onClick={() => goView(View.Mcp)}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 7V3M15 7V3" /><path d="M6 7h12v4a6 6 0 0 1-12 0z" /><path d="M12 17v4" /></svg>
                  <span className="nav-link-label">MCP</span>
                  <NavCount value={mcpCount} />
                </button>
              )}

              {/* Closes the org-scoped section. */}
              <div className="nav-separator" />
            </>
          )}
        </nav>
        {/* Pinned to the bottom of the rail: the admin-only Settings link (global),
            then the signed-in user + sign-out directly below it. */}
        {isAppAdmin(me) && (
          <button className={`nav-link nav-settings${view === View.Settings ? " active" : ""}`} onClick={() => goView(View.Settings)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg>
            <span className="nav-link-label">Settings</span>
          </button>
        )}
        <UserFooter />
      </div>
    </aside>
  );
}
